/**
 * Role-Based Access Control (RBAC) — client-side nav filter + guards.
 *
 * Roles (matches backend seed_data.py user list):
 *   MD               — Owner · FULL edit rights on every module (super-user #1)
 *   CFO              — Full access (super-user #2)
 *   ProjectManager   — Projects, procurement, HR/labour on-site only
 *   Accountant       — Vouchers, journals, ledger, banking, statements, GST, payroll
 *
 * Each nav item's `roles` array lists which roles CAN see it. Empty/missing = all roles.
 */

export const ROLES = {
    MD: "MD",
    CFO: "CFO",
    PM: "ProjectManager",
    ACC: "Accountant",
};

export const ALL_ROLES = [ROLES.MD, ROLES.CFO, ROLES.PM, ROLES.ACC];

export const ROLE_LABELS = {
    [ROLES.MD]: "Managing Director",
    [ROLES.CFO]: "Chief Financial Officer",
    [ROLES.PM]: "Project Manager",
    [ROLES.ACC]: "Senior Accountant",
};

export const ROLE_COLORS = {
    [ROLES.MD]: "text-[#fb8b1e]",       // amber
    [ROLES.CFO]: "text-[#4af6c3]",       // green
    [ROLES.PM]: "text-[#60a5fa]",        // blue
    [ROLES.ACC]: "text-[#c084fc]",       // purple
};

/**
 * Route → allowed roles map. MD and CFO have full access to every route.
 * PM and Accountant remain scoped to their functional areas.
 */
export const ROUTE_ACCESS = {
    "/": ALL_ROLES,
    "/clients": ALL_ROLES,
    "/workbook-ingest": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/data-quality": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/enrich": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/intelligence": [ROLES.MD, ROLES.CFO],
    "/simulator": [ROLES.MD, ROLES.CFO],
    "/decisions": [ROLES.MD, ROLES.CFO],
    "/entry": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/procurement": [ROLES.MD, ROLES.CFO, ROLES.PM, ROLES.ACC],
    "/sales": [ROLES.MD, ROLES.CFO, ROLES.PM, ROLES.ACC],
    "/payroll": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/gst": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/bank-import": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/vault": ALL_ROLES,
    "/agents": [ROLES.MD, ROLES.CFO, ROLES.ACC, ROLES.PM],
    "/analytics": [ROLES.MD, ROLES.CFO],
    "/tds-retention": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/pnl": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/cheques": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/notifications": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/data": [ROLES.MD, ROLES.CFO, ROLES.ACC, ROLES.PM],
    "/team": [ROLES.MD, ROLES.CFO],
    "/settings": [ROLES.MD, ROLES.CFO],
    "/finance": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/journals": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/ledger": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/statements": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/audit": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/banking": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/cash": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/reconciliation": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/loans": [ROLES.MD, ROLES.CFO],
    "/receivables": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/payables": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/projects": ALL_ROLES,
    "/project-billing": [ROLES.MD, ROLES.CFO, ROLES.ACC, ROLES.PM],
    "/inventory": [ROLES.MD, ROLES.CFO, ROLES.PM, ROLES.ACC],
    "/consumption": [ROLES.MD, ROLES.CFO, ROLES.PM, ROLES.ACC],
    "/equipment": [ROLES.MD, ROLES.CFO, ROLES.PM],
    "/hr": [ROLES.MD, ROLES.CFO, ROLES.PM, ROLES.ACC],
    "/compliance": [ROLES.MD, ROLES.CFO, ROLES.ACC],
    "/tenders": [ROLES.MD, ROLES.CFO, ROLES.PM],
    "/risk": [ROLES.MD, ROLES.CFO],
    "/ratios": [ROLES.MD, ROLES.CFO],
    "/growth": [ROLES.MD, ROLES.CFO],
    "/ai-advisor": [ROLES.MD, ROLES.CFO],
};

export function can(role, route) {
    if (!role) return false;
    // MD and CFO are super-users — always allowed everywhere
    if (role === ROLES.MD || role === ROLES.CFO) return true;
    const allowed = ROUTE_ACCESS[route];
    if (!allowed) return true; // default open
    return allowed.includes(role);
}
